import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useGroup } from '../contexts/GroupContext';

/**
 * Custom hook that extends useQuery to include the selected group in the query key.
 * This ensures proper caching when switching between groups.
 * 
 * @param baseQueryKey The base query key (without the group)
 * @param queryFn The query function to execute
 * @param options Additional useQuery options
 * @returns The useQuery result
 */
export function useGroupQuery<TData, TError = unknown>(
  baseQueryKey: unknown[],
  queryFn: () => Promise<TData>,
  options?: Omit<UseQueryOptions<TData, TError, TData>, 'queryKey' | 'queryFn'>
): UseQueryResult<TData, TError> {
  const { selectedGroup, isLoading: isGroupLoading } = useGroup();
  
  // Include the selected group in the query key
  const queryKey = [...baseQueryKey, selectedGroup];
  
  return useQuery({
    queryKey,
    queryFn,
    // The query is enabled only when a group is selected and group loading is complete
    enabled: !!selectedGroup && !isGroupLoading && (options?.enabled !== false),
    // Add staleTime to prevent unnecessary refetches
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep the previous data while loading new data
    placeholderData: (previousData) => previousData,
    ...options,
  });
} 